import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Modal,
  Avatar,
  Chip,
  Fade,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Header from '../components/Header';
import { useSnackbar } from '../components/SnackbarProvider';
import { Demandes } from '../models/Demandes';
import { recupererListeVisasUtilisateur } from '../Services';
import { FILES_BASE_URL } from '../config/api';
import { MdFileDownload, MdShare, MdOpenInNew, MdCreditCard } from 'react-icons/md';
import DescriptionIcon from '@mui/icons-material/Description';
import CardSkeleton from '../components/CardSkeleton';
import CloseIcon from '@mui/icons-material/Close';
import WifiOffIcon from '@mui/icons-material/WifiOff';

interface OfflineVisa {
  fileName: string;
  pdfUrl: string;
}

const MesVisasScreen: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const [demandes, setDemandes] = useState<Demandes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadedVisas, setDownloadedVisas] = useState<string[]>([]);
  const [offlineVisas, setOfflineVisas] = useState<OfflineVisa[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(navigator.onLine);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const fetchDemandes = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const userId = user.id_utilisateur;
        const demandesData = await recupererListeVisasUtilisateur(userId);
        setDemandes(demandesData);
        setIsLoading(false);
        const downloadedVisasData = localStorage.getItem('@downloadedVisas');
        if (downloadedVisasData) {
          setDownloadedVisas(JSON.parse(downloadedVisasData));
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
    }
  };

  const fetchOfflineVisas = () => {
    const offlineVisasData = localStorage.getItem('@offlineVisas');
    if (offlineVisasData) {
      setOfflineVisas(JSON.parse(offlineVisasData));
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchDemandes();
    fetchOfflineVisas();
  }, []);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const downloadFile = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      showSnackbar(
        'Téléchargement réussi. Votre visa est bien enregistré sur votre mobile et sera accessible même hors connexion.',
        'success'
      );
      const updatedDownloadedVisas = [...downloadedVisas, fileName];
      setDownloadedVisas(updatedDownloadedVisas);
      localStorage.setItem('@downloadedVisas', JSON.stringify(updatedDownloadedVisas));

      const newOfflineVisa: OfflineVisa = { fileName, pdfUrl: url };
      const updatedOfflineVisas = [...offlineVisas, newOfflineVisa];
      setOfflineVisas(updatedOfflineVisas);
      localStorage.setItem('@offlineVisas', JSON.stringify(updatedOfflineVisas));
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier:', error);
      showSnackbar('Une erreur est survenue lors du téléchargement du fichier.', 'error');
    }
  };

  const shareFile = async (fileName: string) => {
    const offlineVisa = offlineVisas.find((v) => v.fileName === fileName);
    const fileUrl = offlineVisa ? offlineVisa.pdfUrl : '';
    if (navigator.share && fileUrl) {
      try {
        await navigator.share({
          title: 'Visa',
          text: 'Voici mon visa',
          url: fileUrl,
        });
      } catch (error) {
        console.error('Erreur lors du partage du fichier:', error);
      }
    } else {
      showSnackbar("Le partage n'est pas supporté sur ce navigateur.", 'warning');
    }
  };

  const handlePressCard = (chemin: string) => {
    const url = `${FILES_BASE_URL}/demandes/${chemin}/visa.pdf`;
    window.open(url, '_blank');
  };

  const handlePressEcard = (chemin: string) => {
    const url = `${FILES_BASE_URL}/demandes/${chemin}/ecard.pdf`;
    window.open(url, '_blank');
  };

  const closePdf = () => {
    setSelectedPdf(null);
    setIsModalVisible(false);
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      <Header />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Page Title (desktop) */}
        {isDesktop && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: 'white', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
            >
              Mes Visas
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
            >
              Consultez et téléchargez vos visas
            </Typography>
          </Box>
        )}

        {/* PDF Modal */}
        <Modal open={isModalVisible} onClose={closePdf}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '85%', md: '75%' },
              height: '85%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 3,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <IconButton onClick={closePdf} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            {selectedPdf && (
              <iframe
                src={selectedPdf}
                title="Visa PDF"
                style={{ flex: 1, border: 'none', borderRadius: 8 }}
              />
            )}
          </Box>
        </Modal>

        {isConnected ? (
          isLoading ? (
            <CardSkeleton count={3} variant="demande" />
          ) : demandes.length === 0 ? (
            /* ===== Empty state ===== */
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 3,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                backdropFilter: 'blur(8px)',
                maxWidth: 500,
                mx: 'auto',
                mt: 4,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#555', mb: 1 }}>
                Aucun visa traité
              </Typography>
              <Typography variant="body2" sx={{ color: '#999' }}>
                Vos visas approuvés apparaîtront ici
              </Typography>
            </Box>
          ) : (
            /* ===== Visa cards grid ===== */
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
              {demandes.map((item, index) => {
                const fileName = `${item.titre}_${item.prenom}_${item.nom}.pdf`;
                const isEvisa = item.titre.toLowerCase().includes('evisa');
                // Le lien E-card s'affiche dès qu'un ecard.pdf existe dans le dossier,
                // quel que soit le type de visa (le backend fait le test d'existence).
                const showEcard = !!item.hasEcard;

                return (
                  <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                    <Fade in={true} timeout={Math.min(300 + index * 100, 800)}>
                    <Card
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        width: '100%',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: isDesktop ? 'translateY(-4px)' : 'none',
                          boxShadow: isDesktop
                            ? '0 8px 30px rgba(0,0,0,0.15)'
                            : '0 2px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      {/* Top accent */}
                      <Box
                        sx={{
                          height: 4,
                          background: 'linear-gradient(90deg, #2e7d32, #43a047)',
                        }}
                      />

                      <CardContent sx={{ p: 2.5, flex: 1 }}>
                        {/* Dossier number + badge */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={`Dossier Nº ${item.id}`}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                            }}
                          />
                          {isEvisa && (
                            <Chip
                              label="eVisa"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                backgroundColor: '#2e7d32',
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>

                        {/* Flag + Title */}
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                              mb: 1,
                            }}
                          >
                            <img
                              src={item.urlflag}
                              alt="flag"
                              style={{
                                width: 56,
                                height: 36,
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.95rem', sm: '1.05rem' },
                              color: '#333',
                              lineHeight: 1.3,
                            }}
                          >
                            {item.titre}
                          </Typography>
                        </Box>

                        {/* Traveler info */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            mb: 2,
                            py: 1.5,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                          }}
                        >
                          <Avatar
                            src={item.urlvoyageur}
                            alt={`${item.nom} ${item.prenom}`}
                            sx={{
                              width: 44,
                              height: 44,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              border: '2px solid white',
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#444' }}
                          >
                            {item.prenom} {item.nom}
                          </Typography>
                        </Box>

                        {/* Dates & Info */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              Départ
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: '#444' }}
                            >
                              {item.datedepart}
                            </Typography>
                          </Box>

                          {item.datevalidite && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                Valide du
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                {formatDate(item.datevalidite)}
                              </Typography>
                            </Box>
                          )}

                          {item.dateexpiration && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                Au
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                {formatDate(item.dateexpiration)}
                              </Typography>
                            </Box>
                          )}

                          {item.dateexpiration && (() => {
                            const daysRemaining = Math.ceil(
                              (new Date(item.dateexpiration).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                            );
                            let label: string;
                            let chipColor: string;
                            let chipBg: string;
                            if (daysRemaining <= 0) {
                              label = 'Expiré';
                              chipColor = '#c62828';
                              chipBg = '#ffebee';
                            } else if (daysRemaining <= 30) {
                              label = `Expire dans ${daysRemaining} jours`;
                              chipColor = '#e65100';
                              chipBg = '#fff3e0';
                            } else {
                              label = `Expire dans ${daysRemaining} jours`;
                              chipColor = '#2e7d32';
                              chipBg = '#e8f5e9';
                            }
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                <Chip
                                  label={label}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: chipColor,
                                    backgroundColor: chipBg,
                                  }}
                                />
                              </Box>
                            );
                          })()}
                        </Box>

                        {item.infosvisa && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1.5,
                              color: '#888',
                              textAlign: 'center',
                              lineHeight: 1.4,
                            }}
                          >
                            {item.infosvisa}
                          </Typography>
                        )}
                      </CardContent>

                      {/* Action buttons */}
                      <CardActions
                        sx={{
                          px: 2,
                          pb: 2,
                          pt: 0,
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {isEvisa && (
                          <Button
                            variant="contained"
                            startIcon={<MdOpenInNew />}
                            onClick={() => handlePressCard(item.chemin)}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                              boxShadow: '0 2px 8px rgba(46,125,50,0.25)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                              },
                            }}
                          >
                            Accéder à mon visa
                          </Button>
                        )}

                        {showEcard && (
                          <Button
                            variant="outlined"
                            startIcon={<MdCreditCard />}
                            onClick={() => handlePressEcard(item.chemin)}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                              borderColor: '#2e7d32',
                              color: '#2e7d32',
                              '&:hover': {
                                borderColor: '#1b5e20',
                                backgroundColor: 'rgba(46,125,50,0.04)',
                              },
                            }}
                          >
                            Accéder à ma E-card
                          </Button>
                        )}

                        {downloadedVisas.includes(fileName) && (
                          <Button
                            variant="outlined"
                            startIcon={<MdShare />}
                            onClick={() => shareFile(fileName)}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                              borderColor: '#2e7d32',
                              color: '#2e7d32',
                              '&:hover': {
                                borderColor: '#1b5e20',
                                backgroundColor: 'rgba(46,125,50,0.04)',
                              },
                            }}
                          >
                            Partager
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          /* ===== Offline mode ===== */
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 2,
                backgroundColor: 'rgba(255,152,0,0.15)',
                borderRadius: 2,
                backdropFilter: 'blur(8px)',
              }}
            >
              <WifiOffIcon sx={{ color: '#e65100' }} />
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                Mode hors connexion — visas téléchargés uniquement
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {offlineVisas.map((visa, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: isDesktop ? 'translateY(-4px)' : 'none',
                        boxShadow: isDesktop
                          ? '0 8px 30px rgba(0,0,0,0.15)'
                          : '0 2px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                    onClick={() => {
                      setSelectedPdf(visa.pdfUrl);
                      setIsModalVisible(true);
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        background: 'linear-gradient(90deg, #ff9800, #ffa726)',
                      }}
                    />
                    <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                      <DescriptionIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#444', wordBreak: 'break-word' }}
                      >
                        {visa.fileName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
                        Appuyez pour ouvrir
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MesVisasScreen;
